import { DEFAULT_ASSET_LATITUDE, DEFAULT_ASSET_LONGITUDE } from '../constants/assetDefaultGeo';
import { findIsraelSettlementByWire } from './israelSettlements';

export type GeoCoordinates = { lat: number; lng: number };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function parseGeoNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.trim().replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Read root-level `latitude` / `longitude` from an asset record or editor body. */
export function parseAssetCoordinates(record: Record<string, unknown>): GeoCoordinates | null {
  const lat =
    parseGeoNumber(record.latitude) ??
    parseGeoNumber(record.lat) ??
    parseGeoNumber(record.Latitude);
  const lng =
    parseGeoNumber(record.longitude) ??
    parseGeoNumber(record.lng) ??
    parseGeoNumber(record.Longitude);
  if (lat !== null && lng !== null) {
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    return { lat, lng };
  }

  const location = record.location;
  if (isPlainObject(location)) {
    const locLat =
      parseGeoNumber(location.lat) ??
      parseGeoNumber(location.latitude) ??
      parseGeoNumber(location.Lat);
    const locLng =
      parseGeoNumber(location.lon) ??
      parseGeoNumber(location.lng) ??
      parseGeoNumber(location.longitude) ??
      parseGeoNumber(location.Lng);
    if (locLat !== null && locLng !== null) {
      if (locLat < -90 || locLat > 90 || locLng < -180 || locLng > 180) {
        return null;
      }
      return { lat: locLat, lng: locLng };
    }
  }

  const addr = record.address;
  if (isPlainObject(addr)) {
    const addrLat = parseGeoNumber(addr.latitude) ?? parseGeoNumber(addr.lat);
    const addrLng = parseGeoNumber(addr.longitude) ?? parseGeoNumber(addr.lng);
    if (addrLat !== null && addrLng !== null) {
      if (addrLat < -90 || addrLat > 90 || addrLng < -180 || addrLng > 180) {
        return null;
      }
      return { lat: addrLat, lng: addrLng };
    }
  }
  return null;
}

export function isDefaultJerusalemCoordinates(coords: GeoCoordinates): boolean {
  return (
    Math.abs(coords.lat - DEFAULT_ASSET_LATITUDE) < 0.0001 &&
    Math.abs(coords.lng - DEFAULT_ASSET_LONGITUDE) < 0.0001
  );
}

export type AddressGeocodeLabels = {
  countryLabel?: string | null;
  regionLabel?: string | null;
};

export type BuildAddressGeocodeQueryOptions = {
  /** When false, omits `address.address2` (additional address line). Default true. */
  includeAddress2?: boolean;
};

function pickTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Street + building line from a nested address object. */
export function formatAssetAddressStreetLine(addr: Record<string, unknown>): string {
  const street = pickTrimmedString(addr.street);
  const building =
    pickTrimmedString(addr.building) ||
    pickTrimmedString(addr.street_number) ||
    pickTrimmedString(addr.house_number);
  return [street, building].filter(Boolean).join(' ');
}

/** Build a single-line query for geocoding from the asset address object. */
export function buildAddressGeocodeQuery(
  body: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
  options?: BuildAddressGeocodeQueryOptions,
): string | null {
  const addr = body.address;
  if (!isPlainObject(addr)) {
    return null;
  }
  const streetLine = formatAssetAddressStreetLine(addr);
  const line2 = pickTrimmedString(addr.address2);
  const city = pickTrimmedString(addr.city);
  if (!city) {
    return null;
  }
  const includeAddress2 = options?.includeAddress2 !== false;

  const settlement = findIsraelSettlementByWire(city);
  const parts: string[] = [];
  if (streetLine) {
    parts.push(streetLine);
  }
  if (includeAddress2 && line2) {
    parts.push(line2);
  }

  if (settlement) {
    parts.push(settlement.he);
    if (settlement.nafaName?.trim()) {
      parts.push(settlement.nafaName.trim());
    }
    const country = labels?.countryLabel?.trim() || 'ישראל';
    parts.push(country);
    return streetLine ? parts.join(', ') : null;
  }

  if (city) {
    parts.push(city);
  }
  const zip = pickTrimmedString(addr.zip_code);
  if (zip) {
    parts.push(zip);
  }
  if (labels?.regionLabel?.trim()) {
    parts.push(labels.regionLabel.trim());
  }
  if (labels?.countryLabel?.trim()) {
    parts.push(labels.countryLabel.trim());
  }
  if (!city || parts.length < 2) {
    return null;
  }
  return parts.join(', ');
}

export function googleMapsSearchUrlForQuery(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function googleMapsSearchUrlForCoordinates(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/** Great-circle distance in kilometres (Haversine). */
export function distanceKmBetween(a: GeoCoordinates, b: GeoCoordinates): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

const KM_PER_MILE = 0.621371;

function formatDistanceAmount(value: number): string {
  const useDecimal = value < 10;
  if (useDecimal) {
    return value.toFixed(1);
  }
  const rounded = Math.round(value);
  return rounded >= 1000 ? rounded.toLocaleString('en-US') : String(rounded);
}

export function formatDistanceFromKm(km: number, language: string): string {
  if (language === 'he') {
    return `${formatDistanceAmount(km)} ק"מ`;
  }
  const miles = km * KM_PER_MILE;
  return `${formatDistanceAmount(miles)} mi`;
}

export function formatAssetDistanceLabel(
  asset: Record<string, unknown>,
  viewer: GeoCoordinates | null | undefined,
  language: string,
  unknownLabel: string,
): string | null {
  if (!viewer) {
    return null;
  }
  const assetCoords = parseAssetCoordinates(asset);
  if (!assetCoords) {
    return null;
  }
  const km = distanceKmBetween(viewer, assetCoords);
  return formatDistanceFromKm(km, language);
}

export function applyCoordinatesToAssetBody(
  body: Record<string, unknown>,
  coords: GeoCoordinates,
): Record<string, unknown> {
  return {
    ...body,
    latitude: coords.lat,
    longitude: coords.lng,
  };
}
